import { memo } from 'react'
import { Handle, Position, NodeProps } from '@xyflow/react'

export interface NodeRedNodeData {
  label: string
  type: string
  color: string
  outputCount?: number
}

function NodeRedNode({ data, selected }: NodeProps) {
  const { label, color, outputCount = 1 } = data as unknown as NodeRedNodeData

  return (
    <div
      className={`
        relative rounded-lg
        ${selected
          ? 'ring-2 ring-primary-400 ring-offset-2 shadow-xl'
          : 'shadow-md hover:shadow-lg'
        }
      `}
      style={{
        backgroundColor: color,
        minWidth: '120px',
        maxWidth: '200px',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      }}
    >
      {/* Input handle (left side) */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-3 !h-3 !bg-white !border-2 !-left-1.5 hover:!w-4 hover:!h-4"
        style={{
          borderColor: selected ? '#38bdf8' : color,
          transition: 'width 0.15s ease, height 0.15s ease',
        }}
      />

      {/* Node content */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          {/* Label with ellipsis */}
          <div
            className="text-sm font-medium text-white leading-tight truncate"
            style={{
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
            title={label}
          >
            {label}
          </div>

          {/* Output count badge */}
          {outputCount > 1 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-white/30 text-white flex-shrink-0">
              {outputCount}×
            </span>
          )}
        </div>
      </div>

      {/* Selection indicator */}
      {selected && (
        <div
          className="absolute -inset-0.5 rounded-lg opacity-20 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.4), rgba(168, 85, 247, 0.4))',
          }}
        />
      )}

      {/* Output handles (right side) */}
      {Array.from({ length: outputCount }).map((_, index) => {
        const totalOutputs = outputCount
        const position = totalOutputs === 1
          ? '50%'
          : `${((index + 1) / (totalOutputs + 1)) * 100}%`

        return (
          <Handle
            key={`output-${index}`}
            type="source"
            position={Position.Right}
            id={`output-${index}`}
            className="!w-3 !h-3 !bg-white !border-2 !-right-1.5 hover:!w-4 hover:!h-4"
            style={{
              borderColor: selected ? '#38bdf8' : color,
              top: position,
              transform: 'translateY(-50%)',
              transition: 'width 0.15s ease, height 0.15s ease',
            }}
          />
        )
      })}

      {/* Glow effect on hover */}
      <div
        className="absolute inset-0 rounded-lg opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          boxShadow: `0 0 20px ${color}40`,
        }}
      />
    </div>
  )
}

export default memo(NodeRedNode)
